import { Text, View } from '@react-pdf/renderer';
import { Term } from '../../../../models/courses/term';
import { CoursePDFStyles } from '../course-details-pdf-css';
import { MessageKeys } from 'next-intl';
import { CountryCode } from '../../../../constants/country';
import { formatMonthFromDate } from '../../../../utils/string-helpers/formatting.helper';
import { CourseInfo } from '../../../../models/courses/course-info';

const ImportantDatesTermsItem: React.FC<{
  term: Term;
  index: number;
  styles: CoursePDFStyles;
  brandTermName: string;
}> = ({ term, index, brandTermName, styles }) => {
  return (
    <>
      <View key={`dateV-${index}`}>
        <Text key={`sdate-${index}`} style={[styles.paragraphDate]}>
          {!!term.seasonDescription && (
            <>
              {brandTermName} {index + 1}
              {': '}
            </>
          )}

          {term.startDateString}
          {' - '}
          {term.endDateString}
        </Text>
      </View>
    </>
  );
};

const ImportantDatesTermsItemUS: React.FC<{
  term: Term;
  index: number;
  styles: CoursePDFStyles;
  brandTermName: string;
  translate: (translationKey: MessageKeys<any, any>) => string;
}> = ({ term, index, brandTermName, styles, translate }) => {
  return (
    <>
      <View key={`dateV-${index}`}>
        <Text key={`sdate-${index}`} style={[styles.paragraphDate]}>
          {!!term.seasonDescription && (
            <>
              {brandTermName} {index + 1}{' '}
              {translate(
                term.seasonDescription.translationKey as MessageKeys<any, any>,
              ).toLowerCase()}
              {': '}
            </>
          )}

          {term.startDateString}
          {' - '}
          {term.endDateString}
        </Text>
      </View>
    </>
  );
};

export function generateImportantDatesRow(
  terms: Term[],
  programLength: string,
  locale: string,
  styles: CoursePDFStyles,
) {
  const datesRow = terms.map((term, index) => {
    const forFirstRow =
      index === 0 ? (
        <Text key={`duration-${index}`} style={[styles.dateColumn]}>
          <Text style={styles.paragraph}>{programLength}</Text>
        </Text>
      ) : null;

    return (
      <View key={`dateV-${index}`} style={[styles.tableRow, styles.tableRowMargin10]}>
        <Text key={`sdate-${index}`} style={[styles.dateColumn]}>
          <Text style={styles.paragraph}>{term.startDateString}</Text>
        </Text>
        <Text key={`edate-${index}`} style={[styles.dateColumn]}>
          <Text style={styles.paragraph}>{term.endDateString}</Text>
        </Text>
        {forFirstRow}
      </View>
    );
  });

  return (
    <>
      <View style={styles.tableRow}>
        <Text style={[styles.dateColumn]}>
          <Text style={styles.paragraphHeading}>Start Date</Text>
        </Text>
        <Text style={[styles.dateColumn]}>
          <Text style={styles.paragraphHeading}>End Date</Text>
        </Text>
        <Text style={[styles.dateColumn]}>
          <Text style={styles.paragraphHeading}>Length</Text>
        </Text>
      </View>
      <View key={`dates`}>{datesRow}</View>
    </>
  );
}

const ImportantDatesHeader: React.FC<{
  courseInfo: CourseInfo;
  translate: (translationKey: MessageKeys<any, any>) => string;
  styles: CoursePDFStyles;
}> = ({ courseInfo, translate, styles }) => {
  const firstTerm = courseInfo.terms[0];
  const lastTerm = courseInfo.terms[courseInfo.terms.length - 1];

  return (
    <>
      <View>
        <Text style={[styles.paragraphDateHeading]}>
          <>
            {translate('Courses.importantDates.academicYear')} {courseInfo.academicYear}
          </>
        </Text>
      </View>
      <View>
        <Text style={[styles.paragraphDate]}>
          {firstTerm.startDateString}
          {' - '}
          {lastTerm.endDateString}
        </Text>
      </View>
    </>
  );
};

const ImportantDatesHeaderUS: React.FC<{
  courseInfo: CourseInfo;
  translate: (translationKey: MessageKeys<any, any>) => string;
  styles: CoursePDFStyles;
}> = ({ courseInfo, translate, styles }) => {
  const firstTerm = courseInfo.terms[0];
  const lastTerm = courseInfo.terms[courseInfo.terms.length - 1];

  return (
    <>
      <View>
        <Text style={[styles.paragraphDateHeading]}>
          <>
            {firstTerm.startDate.getFullYear()}{' '}
            {translate(firstTerm.seasonDescription.translationKey as MessageKeys<any, any>)}
            {' Start ('}
            {formatMonthFromDate(firstTerm.startDate, firstTerm.locale)}
            {')'}
          </>
        </Text>
      </View>
      <View>
        <Text style={[styles.paragraphDate]}>
          {firstTerm.countryCode == CountryCode.US
            ? translate('Courses.importantDates.courseSynonymUS')
            : translate('Courses.importantDates.courseSynonymUK')}{' '}
          {translate('Courses.importantDates.startDate')} : {firstTerm.startDateString} -{' '}
          {firstTerm.countryCode == CountryCode.US
            ? translate('Courses.importantDates.courseSynonymUS')
            : translate('Courses.importantDates.courseSynonymUK')}{' '}
          {translate('Courses.importantDates.endDate')}: {lastTerm.endDateString}
        </Text>
      </View>
    </>
  );
};

export function generateImportantDatesRowUSPathway(
  courseInfos: CourseInfo[],
  brandTermName: string,
  translate: (translationKey: MessageKeys<any, any>) => string,
  styles: CoursePDFStyles,
  isCountryUS: boolean,
) {
  return (
    <View key={`dates`}>
      {courseInfos?.map((courseInfo, index) => (
        <>
          {isCountryUS ? (
            <ImportantDatesHeaderUS courseInfo={courseInfo} translate={translate} styles={styles} />
          ) : (
            <ImportantDatesHeader courseInfo={courseInfo} translate={translate} styles={styles} />
          )}

          {courseInfo.terms.map((term, indexTerm) => (
            <>
              {isCountryUS ? (
                <ImportantDatesTermsItemUS
                  key={term.id}
                  term={term}
                  index={indexTerm}
                  styles={styles}
                  brandTermName={brandTermName}
                  translate={translate}
                />
              ) : (
                <ImportantDatesTermsItem
                  key={term.id}
                  term={term}
                  index={indexTerm}
                  styles={styles}
                  brandTermName={brandTermName}
                />
              )}
            </>
          ))}

          {index !== courseInfo.terms.length - 1 && (
            <View style={[styles.tableRow, styles.divider]}></View>
          )}
        </>
      ))}
    </View>
  );
}
